import { useEffect, useMemo, useState } from 'react';
import { Loader2, Trophy } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

/**
 * SAB-288 Phase 6 — Per-team bonus earnings panel.
 *
 * Renders the "Total Bonuses Paid" header card + a per-team list sorted
 * by total earnings desc. Reads from bonus_awards directly; RLS gates
 * visibility to league members (verified Phase 1).
 */

export interface BonusesPanelTeam {
  id: string;
  name: string;
  owner_id: string;
}

const TEAM_COLOR_PALETTE = [
  '#F97316', '#EF4444', '#22C55E', '#3B82F6', '#A855F7',
  '#EAB308', '#EC4899', '#14B8A6', '#F472B6', '#94A3B8',
];

function hashId(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

function teamAvatar(name: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9]/g, '');
  return (cleaned.slice(0, 2) || 'TM').toUpperCase();
}

function teamColor(id: string): string {
  return TEAM_COLOR_PALETTE[hashId(id) % TEAM_COLOR_PALETTE.length];
}

function fmtUSD(n: number): string {
  return '$' + n.toLocaleString('en-US', {
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

export function BonusesPanel({
  leagueId,
  teams,
  currentWeek,
}: {
  leagueId: string;
  teams: BonusesPanelTeam[];
  currentWeek: number;
}) {
  const [earnings, setEarnings] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const { data, error } = await supabase
        .from('bonus_awards' as any)
        .select('recipient_team_id, amount')
        .eq('league_id', leagueId);
      if (cancelled) return;
      if (error) {
        setEarnings({});
        setLoading(false);
        return;
      }
      const acc: Record<string, number> = {};
      for (const row of (data ?? []) as Array<{
        recipient_team_id: string;
        amount: number | string;
      }>) {
        acc[row.recipient_team_id] =
          (acc[row.recipient_team_id] ?? 0) + Number(row.amount);
      }
      setEarnings(acc);
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  const sortedTeams = useMemo(
    () =>
      [...teams].sort((a, b) => (earnings[b.id] ?? 0) - (earnings[a.id] ?? 0)),
    [teams, earnings],
  );
  const totalAwarded = useMemo(
    () => Object.values(earnings).reduce((s, v) => s + v, 0),
    [earnings],
  );
  const earnerCount = useMemo(
    () => Object.values(earnings).filter((v) => v > 0).length,
    [earnings],
  );

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 flex items-center justify-center text-muted-foreground gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading earnings…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Total card */}
      <div
        className={cn(
          'rounded-xl p-4 border',
          'bg-gradient-to-br from-amber-500/8 to-orange-500/4 border-amber-500/25',
        )}
      >
        <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-amber-400">
          Total Bonuses Paid
        </div>
        <div className="font-bold text-3xl mt-1 tabular-nums tracking-tight">
          {fmtUSD(totalAwarded)}
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5">
          across {earnerCount} {earnerCount === 1 ? 'manager' : 'managers'} ·
          weeks 1–{currentWeek}
        </div>
      </div>

      {/* Per-team list */}
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground mb-2">
          Per-Team Earnings
        </div>
        {teams.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
            <Trophy className="w-7 h-7 mx-auto mb-2 text-muted-foreground/50" />
            No teams in this league yet.
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            {sortedTeams.map((t, i) => {
              const amt = earnings[t.id] ?? 0;
              const isPositive = amt > 0;
              return (
                <div
                  key={t.id}
                  className={cn(
                    'grid items-center gap-2.5 px-3.5 py-3',
                    'grid-cols-[24px_28px_1fr_auto]',
                    i > 0 && 'border-t border-border/60',
                  )}
                >
                  <div className="text-center text-xs font-bold text-muted-foreground tabular-nums">
                    {i + 1}
                  </div>
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-[10px]"
                    style={{ background: teamColor(t.id), color: '#0B1322' }}
                  >
                    {teamAvatar(t.name)}
                  </div>
                  <div className="font-bold text-sm truncate">{t.name}</div>
                  <div
                    className={cn(
                      'font-bold text-sm tabular-nums',
                      isPositive ? 'text-amber-400' : 'text-muted-foreground/60',
                    )}
                  >
                    {isPositive ? `+${fmtUSD(amt)}` : '-'}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
